import axios from "axios";
import debugData from "../data/debugData.json";
import { unifiedLogout } from "../services/logoutService";
import { translateApiPayload } from "../utils/translateApiError";
import { getUserFacingApiMessage } from "../utils/getUserFacingApiMessage";
import i18n from "../i18n/config";

const DEBUG_MODE = false;

/** API base: REACT_APP_API_BASE_URL sobrescreve; senão dev → localhost, build → produção. */
function getApiBaseURL() {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).replace(/\/$/, "");
  }
  return process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://secure.xpro.me";
}

const apiClient = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 50000,
  withCredentials: false,
});

let bearerToken = localStorage.getItem("bearerToken");

/** Espelho do slug do workspace (schema MySQL); usado se `company` foi sobrescrito por id_projeto por engano. */
const WORKSPACE_DATABASE_MIRROR_KEY = "xproWorkspaceDatabase";

/**
 * Header HTTP Company = nome do banco/schema do tenant (slug), nunca id_projeto.
 */
function getWorkspaceDatabaseHeaderValue() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem("company");
    const s = raw != null ? String(raw).trim() : "";
    if (!s) return null;
    if (/^\d+$/.test(s)) {
      const fb = localStorage.getItem(WORKSPACE_DATABASE_MIRROR_KEY);
      return fb != null && String(fb).trim() ? String(fb).trim() : null;
    }
    try {
      localStorage.setItem(WORKSPACE_DATABASE_MIRROR_KEY, s);
    } catch {
      /* ignore */
    }
    return s;
  } catch {
    return null;
  }
}

/**
 * Lê os projetos selecionados em cada requisição. O sessionStorage é preenchido
 * após o mount (ex.: CompanySelector), então não pode ser lido só na importação do módulo.
 */
function getSelectedProjectIdsForRequest() {
  // Em escopos "partner" / "platform" (console/portal), não anexar ids_projetos,
  // pois isso dispara validação de projetos no tenant e gera 403 em massa.
  try {
    const udRaw = typeof localStorage !== "undefined" ? localStorage.getItem("userData") : null;
    if (udRaw) {
      const ud = JSON.parse(udRaw);
      const scope = ud?.scope;
      if (scope === "partner" || scope === "platform") return null;
    }
  } catch {
    /* ignore */
  }

  try {
    const raw =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem("selectedCompanies")
        : null;
    if (raw && raw !== "undefined") {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
      if (Array.isArray(parsed) && parsed.length === 0) {
        console.warn("IDs de empresas inválidos ou vazios.");
      }
    }
  } catch (error) {
    console.error("Erro ao processar IDs do sessionStorage:", error);
  }
  try {
    if (typeof localStorage !== "undefined") {
      const ap = localStorage.getItem("activeProjectId");
      if (ap) {
        const n = parseInt(String(ap), 10);
        if (Number.isFinite(n) && n > 0) return [n];
      }
    }
  } catch {
    /* ignore */
  }
  try {
    const company =
      typeof localStorage !== "undefined" ? localStorage.getItem("company") : null;
    if (company && /^\d+$/.test(String(company).trim())) {
      const n = parseInt(String(company), 10);
      if (Number.isFinite(n) && n > 0) return [n];
    }
  } catch {
    /* ignore */
  }
  return null;
}

// Interceptador de request
apiClient.interceptors.request.use(
  (config) => {
    config.headers["X-Xpro-Client"] = "browser";

    const attachBearer = bearerToken && !config.skipAuthHeader;
    if (attachBearer) {
      config.headers.Authorization = `Bearer ${bearerToken}`;
    }

    const urlStr = config.url != null ? String(config.url) : "";
    const isPublicSignupFlow =
      urlStr.includes("public/signup") ||
      urlStr.includes("public/verify-email") ||
      urlStr.includes("public/resend-signup-verification") ||
      urlStr.includes("public/plan-catalog");
    const skipCompanyHeader = Boolean(config.skipCompanyHeader);
    const companyHeader = skipCompanyHeader ? null : getWorkspaceDatabaseHeaderValue();
    if (companyHeader && !isPublicSignupFlow) {
      config.headers.Company = companyHeader;
    }
    if (isPublicSignupFlow) {
      config.headers.Company = "__signup__";
    }

    const companyIds = getSelectedProjectIdsForRequest();
    if (companyIds && companyIds.length > 0) {
      if (config.method?.toUpperCase() === "GET") {
        const urlAlreadyHasIdsProjetos =
          config.url && String(config.url).includes("ids_projetos");
        // Não sobrescreve se a requisição já enviar ids_projetos na URL ou em params
        // (ex.: busca de produtos relacionados do mesmo projeto do produto em edição)
        if (!urlAlreadyHasIdsProjetos) {
          const params = new URLSearchParams(config.params || {});
          if (!params.has("ids_projetos")) {
            params.set("ids_projetos", companyIds.join(","));
          }
          config.params = params;
        }
      } else if (["POST", "PUT"].includes(config.method?.toUpperCase())) {
        const body = config.data ? { ...config.data } : {};
        // Telefonia (PABX) não usa ids_projetos; manter payload igual ao esperado pelo backend
        const isTelephony = config.url && String(config.url).includes("/telephony/");
        // Produtos: PUT /products/:id não deve receber ids_projetos do contexto automaticamente,
        // pois isso pode carregar múltiplos projetos e o backend rejeita (produto é 1-por-1).
        // Se alguém precisar alterar `ids_projetos` num update de produto, deve enviar explicitamente no body.
        const urlPath = config.url != null ? String(config.url) : "";
        const isProductUpdateById =
          config.method?.toUpperCase() === "PUT" && /^\/products\/\d+\/?$/.test(urlPath);

        if (!isTelephony && !isProductUpdateById && body.ids_projetos == null) {
          body.ids_projetos = companyIds;
        }
        config.data = body;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Endpoints de autenticação: 401 não deve disparar logout no próprio login (a tela mostra o erro).
const isAuthEndpoint = (config) => {
  const url = config?.url ?? "";
  return (
    /^\/login\/?$/.test(url) ||
    /^\/biometric-login\/?$/.test(url) ||
    /public\/signup/.test(url) ||
    /public\/verify-email/.test(url) ||
    /public\/resend-signup-verification/.test(url)
  );
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const isLoginAttempt = isAuthEndpoint(error.config || {});

    // Só 401 indica sessão inválida/expirada. 403 costuma ser “proibido nesta ação” com sessão válida
    // (ex.: POST …/twilio-outbound-call sem permissão de chamada no banco) — não deve deslogar.
    if (status === 401 && !isLoginAttempt) {
      console.error("Sessão expirada/não autorizada. Efetuando logout unificado…");
      await unifiedLogout("unauthorized");
    }

    if (status === 402) {
      const body = error.response?.data;
      const code = typeof body?.code === "string" ? body.code : null;
      const fromBilling = translateApiPayload({ code, params: body?.params });
      const fallbackMsg =
        (typeof body?.message === "string" && body.message) ||
        i18n.t("billingErrors.generic", { ns: "app" });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("xpro:billing-limit-hit", {
            detail: {
              code,
              message: fromBilling || fallbackMsg,
              raw: body || null,
            },
          })
        );
      }
      return Promise.reject({
        message: fromBilling || fallbackMsg,
        status: 402,
        code,
        response: error.response,
      });
    }

    // 403 (proibido) e demais erros agora são propagados para a UI,
    // permitindo exibir mensagens específicas (ex.: sem permissão no projeto).
    try {
      error.userFacing = getUserFacingApiMessage(error);
    } catch {
      /* ignore */
    }
    return Promise.reject(error);
  }
);

export const setBearerToken = (token) => {
  bearerToken = token;
  localStorage.setItem("bearerToken", token);
};

export const clearBearerToken = () => {
  bearerToken = null;
  localStorage.removeItem("bearerToken");
};

export const fetchData = async (endpoint, method = "GET", body = null) => {
  if (DEBUG_MODE) {
    console.log(`[DEBUG MODE] ${method} em ${endpoint}`);
    if (method === "GET") {
      const data = debugData[endpoint];
      if (!data) {
        throw new Error(`Endpoint ${endpoint} não encontrado em debugData.json`);
      }
      return data;
    } else {
      console.log("Simulando operação no DEBUG MODE:", {
        method,
        endpoint,
        body,
      });
      return { success: true };
    }
  }

  try {
    const config = {
      method,
      url: endpoint,
      headers: { "Content-Type": "application/json" },
    };

    if (body) {
      const sanitizedBody = JSON.parse(
        JSON.stringify(body, (key, value) => (value === undefined ? null : value))
      );
      config.data = sanitizedBody;
    }

    const response = await apiClient(config);
    const payload = response.data?.data;
    return {
      data: payload?.data ?? payload ?? [],
      pagination: payload?.pagination,
      totalInDatabase: payload?.totalInDatabase,
    };
  } catch (error) {
    console.error(
      `Erro na API ${method} ${endpoint}:`,
      error.response?.data || error
    );
    const status = error.response?.status;
    const body = error.response?.data;
    if (status === 402 && body && typeof body === "object") {
      const code = typeof body.code === "string" ? body.code : null;
      const fromBilling = translateApiPayload({ code, params: body.params });
      const fallbackMsg =
        (typeof body.message === "string" && body.message) ||
        i18n.t("billingErrors.generic", { ns: "app" });
      throw {
        message: fromBilling || fallbackMsg,
        status: 402,
        code,
      };
    }
    if (body && typeof body === "object") {
      const u = getUserFacingApiMessage(error);
      throw {
        ...body,
        message: u.message,
        userFacingAction: u.action,
        ...(u.integrationDebug ? { integrationDebug: u.integrationDebug } : {}),
      };
    }
    throw error.response?.data || error;
  }
};

/** Prefixo de IDs sintéticos de threads do WhatsApp pessoal (perfil) na lista do Chat. */
export const PERSONAL_WHATSAPP_CONVERSATION_PREFIX = "wa-personal:";

export function getPersonalChatKeyFromSyntheticConversationId(convId) {
  const s = String(convId || "");
  if (!s.startsWith(PERSONAL_WHATSAPP_CONVERSATION_PREFIX)) return null;
  try {
    return decodeURIComponent(s.slice(PERSONAL_WHATSAPP_CONVERSATION_PREFIX.length));
  } catch {
    return null;
  }
}

/** WhatsApp pessoal (perfil): sessão, lista Redis e promover para solicitação */
export async function getPersonalWhatsappStatus() {
  return fetchData("/users/me/whatsapp-personal", "GET");
}

export async function createPersonalWhatsappSession(payload = {}) {
  return fetchData("/users/me/whatsapp-personal/session", "POST", payload);
}

export async function getPersonalWhatsappQrcode() {
  return fetchData("/users/me/whatsapp-personal/qrcode", "GET");
}

export async function deletePersonalWhatsappSession() {
  return fetchData("/users/me/whatsapp-personal/session", "DELETE");
}

export async function getPersonalWhatsappChatsList() {
  const r = await fetchData("/users/me/whatsapp-personal/chats", "GET");
  const inner = r?.data;
  if (inner?.chats && Array.isArray(inner.chats)) return inner.chats;
  if (Array.isArray(inner)) return inner;
  return [];
}

export async function getPersonalWhatsappChatMessages(chatKey) {
  const enc = encodeURIComponent(String(chatKey || ""));
  const r = await fetchData(`/users/me/whatsapp-personal/chats/${enc}/messages`, "GET");
  return r?.data ?? {};
}

export async function sendPersonalWhatsappChatMessage(chatKey, textOrPayload) {
  const enc = encodeURIComponent(String(chatKey || ""));
  const payload =
    typeof textOrPayload === "string" || textOrPayload == null
      ? { text: String(textOrPayload || "").trim() }
      : textOrPayload;
  return fetchData(`/users/me/whatsapp-personal/chats/${enc}/send`, "POST", payload);
}

/** Remove histórico Redis desta conversa do WhatsApp pessoal (não apaga a sessão nem a instância). */
export async function deletePersonalWhatsappChat(chatKey) {
  const enc = encodeURIComponent(String(chatKey || ""));
  return fetchData(`/users/me/whatsapp-personal/chats/${enc}`, "DELETE");
}

export async function promotePersonalWhatsappToRequest(body) {
  return fetchData("/users/me/whatsapp-personal/promote-to-request", "POST", body);
}

/**
 * Formato normalizado de erro de login para a UI.
 * @typedef {{ message: string; status?: number; isNetwork?: boolean }} LoginError
 */

/**
 * Chama o endpoint de login. Em caso de erro, rejeita com um objeto normalizado
 * { message, status?, isNetwork? } para a página exibir mensagem adequada.
 */

/** Catálogo público de planos para landing/signup (sem login). */
export async function fetchPublicPlanCatalog() {
  try {
    const { data } = await apiClient.get("/public/plan-catalog");
    const inner = data?.data ?? data;
    return {
      items: Array.isArray(inner?.items) ? inner.items : [],
      meta: inner?.meta || {},
    };
  } catch {
    return { items: [], meta: {} };
  }
}

/** GET /public/signup — servidor indica se o fluxo de cadastro público está habilitado. */
export async function fetchPublicSignupAvailable() {
  try {
    const { data } = await apiClient.get("/public/signup");
    const inner = data?.data ?? data;
    return !!inner?.publicSignup;
  } catch {
    return false;
  }
}

export const getPublicWebsiteBootstrap = async (slug, company) => {
  const safeSlug = encodeURIComponent(String(slug || "").trim());
  const companyRaw = String(company || "").trim();
  if (!safeSlug) throw new Error("slug é obrigatório para carregar website público.");
  const endpoint = companyRaw
    ? `/public/websites/${safeSlug}/bootstrap?company=${encodeURIComponent(companyRaw)}`
    : `/public/websites/${safeSlug}/bootstrap`;
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

export const getPublicWebsiteBootstrapByHost = async (company = "") => {
  const companyRaw = String(company || "").trim();
  const endpoint = companyRaw
    ? `/public/websites/bootstrap?company=${encodeURIComponent(companyRaw)}`
    : "/public/websites/bootstrap";
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

/**
 * Cadastro público de workspace (API deve ter WORKSPACE_PUBLIC_SIGNUP + template DB).
 * @param {{ workspace, email, password, nome, locale_code?: string, plan?: string, planSku?: string, planVersion?: number, mode?: 'trial'|'pay', audience?: string, partnerToken?: string, acquisition?: object }} payload
 */
export const signupWorkspaceApi = async (payload) => {
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/public/signup",
      data: payload,
      headers: { "Content-Type": "application/json", Company: "__signup__" },
      // Clone do workspace no servidor pode levar vários minutos (offline/RDS).
      timeout: 600000,
    });
    const inner = data?.data ?? data;
    const token = inner?.token;
    const user = inner?.user;
    const company = inner?.company;
    const emailVerificationPending = !!inner?.emailVerificationPending;
    if (token) setBearerToken(token);
    return {
      data: {
        token,
        user,
        company,
        dominio: inner?.dominio,
        emailVerificationPending,
        billingTrial: inner?.billingTrial,
        billingPayFirst: inner?.billingPayFirst,
      },
    };
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data;
    const rawTimeoutHint =
      (typeof body?.errorMessage === "string" && body.errorMessage) ||
      (typeof body?.message === "string" && body.message) ||
      "";
    const looksLikeTimeout =
      status === 504 ||
      status === 408 ||
      err.code === "ECONNABORTED" ||
      /timeout|timed?\s*out/i.test(String(rawTimeoutHint)) ||
      /LambdaTimeoutError/i.test(String(body?.errorType || ""));
    const message =
      (typeof body?.message === "string" && body.message) ||
      (Array.isArray(body?.message) && body.message[0]) ||
      err.message ||
      null;
    const isNetwork = !err.response && (err.code === "ECONNABORTED" || err.message === "Network Error");
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const fromTimeout = looksLikeTimeout
      ? translateApiPayload({ code: "errors.signup.server_timeout" })
      : null;
    const fallback = isNetwork
      ? "Verifique sua conexão e tente novamente."
      : "Não foi possível concluir o cadastro.";
    throw {
      message: fromTimeout || fromCode || message || fallback,
      status: status ?? null,
      isNetwork: !!isNetwork,
      code: typeof body?.code === "string" ? body.code : looksLikeTimeout ? "errors.signup.server_timeout" : null,
    };
  }
};

/**
 * Confirma e-mail após signup (token do link).
 * @param {string} token
 */
export const verifySignupEmailApi = async (token) => {
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/public/verify-email",
      data: { token },
      headers: { "Content-Type": "application/json", Company: "__signup__" },
    });
    const inner = data?.data ?? data;
    return { ok: true, company: inner?.company, message: data?.message };
  } catch (err) {
    const body = err.response?.data;
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const message =
      fromCode ||
      (typeof body?.message === "string" && body.message) ||
      err.message ||
      "Não foi possível confirmar o e-mail.";
    throw { message, code: typeof body?.code === "string" ? body.code : null };
  }
};

/**
 * Reenvia link de verificação (não revela se o e-mail existe).
 */
export const resendSignupVerificationApi = async ({ email, company }) => {
  try {
    await apiClient.request({
      method: "POST",
      url: "/public/resend-signup-verification",
      data: { email: String(email || "").trim(), company: String(company || "").trim().toLowerCase() },
      headers: { "Content-Type": "application/json", Company: "__signup__" },
    });
    return { ok: true };
  } catch (err) {
    const body = err.response?.data;
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const message =
      fromCode ||
      (typeof body?.message === "string" && body.message) ||
      err.message ||
      "Não foi possível reenviar.";
    throw { message, code: typeof body?.code === "string" ? body.code : null };
  }
};

export const loginApi = async (email, password, options = {}) => {
  if (DEBUG_MODE) return { data: { token: "simulated-debug-token", user: {} } };
  try {
    const { data } = await apiClient.request({
      method: "POST",
      url: "/login",
      data: { email, password },
      headers: { "Content-Type": "application/json" },
      skipCompanyHeader: Boolean(options.skipCompanyHeader),
      skipAuthHeader: options.skipAuthHeader !== false,
    });
    const payload = data?.data ?? data;
    const token = payload?.token;
    const user = payload?.user;
    if (token && options.persistToken !== false) setBearerToken(token);
    return { data: { token, user } };
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data;
    const message =
      (typeof body?.message === "string" && body.message) ||
      (Array.isArray(body?.message) && body.message[0]) ||
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.msg === "string" && body.msg) ||
      null;
    const isNetwork = !err.response && (err.code === "ECONNABORTED" || err.message === "Network Error");
    const fallback = isNetwork
      ? "Verifique sua conexão e tente novamente."
      : status === 401
        ? "E-mail ou senha incorretos."
        : status === 403
          ? "Acesso negado para este workspace."
          : status === 404
            ? "Workspace ou serviço de login não encontrado."
            : status >= 500
              ? "Erro temporário no servidor. Tente novamente em instantes."
              : "Não foi possível entrar. Tente novamente.";
    const fromCode = translateApiPayload({
      code: typeof body?.code === "string" ? body.code : null,
      params: body?.params,
    });
    const normalized = {
      message: fromCode || message || fallback,
      status: status ?? null,
      isNetwork: !!isNetwork,
      code: typeof body?.code === "string" ? body.code : null,
    };
    throw normalized;
  }
};

/**
 * Busca mensagens de uma conversa com suporte a paginação.
 * - id: ID da conversa
 * - options:
 *   - limit: quantidade de mensagens por página (padrão backend: 20)
 *   - beforeMessageId: busca mensagens com id < beforeMessageId (para rolagem infinita)
 *   - returnRaw: se true, retorna o payload completo da API
 */
export const getConversationMessagesById = async (id, options = {}) => {
  const { limit, beforeMessageId, returnRaw } = options || {};

  const sp = new URLSearchParams();
  if (limit != null) sp.set("limit", String(limit));
  if (beforeMessageId != null) sp.set("beforeMessageId", String(beforeMessageId));

  const qs = sp.toString();
  const endpoint = `/conversations/${id}/messages${qs ? `?${qs}` : ""}`;
  const response = await fetchData(endpoint, "GET");
  const payload = response?.data ?? response ?? {};

  if (returnRaw) return payload;

  // Compatibilidade com chamadas antigas que esperam apenas o array de mensagens
  if (Array.isArray(payload.messages)) return payload.messages;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

/**
 * Billing - contexto atual do workspace (domínio + assinatura/entitlements).
 */
export const getBillingContext = async () => {
  const { data } = await apiClient.get("/billing/context");
  return data?.data ?? null;
};

/**
 * Billing - resumo de consumo no período.
 * @param {{from?: string, to?: string, includeOverage?: string|number}} params formato YYYY-MM-DD; includeOverage=0 desliga estimativa de excedentes.
 */
export const getBillingUsageSummary = async (params = {}) => {
  const { data } = await apiClient.get("/billing/usage/summary", { params });
  return data?.data ?? null;
};

/**
 * Billing Admin - catálogo de planos ativo.
 */
export const getBillingPlanCatalog = async () => {
  const { data } = await apiClient.get("/billing/admin/plans");
  return data?.data ?? { items: [] };
};

/** Master / billing admin — lista xprobase.dominios (painel plataforma). */
export const listPlatformDominios = async (params = {}) => {
  const merged = {
    ...params,
    tenantLegacyStats: params.tenantLegacyStats != null ? params.tenantLegacyStats : "1",
  };
  const { data } = await apiClient.get("/billing/admin/dominios", { params: merged });
  return data?.data ?? { items: [], meta: {} };
};

/** Master — provisiona workspace (clone do template + dominios + permissoes quando aplicável). */
export const postPlatformCreateWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/dominios", payload || {});
  return data?.data ?? null;
};

/** Master — dispara auto-migrate em workspaces selecionados (ou todos). */
export const postPlatformAutoMigrate = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/auto-migrate", payload || {}, { timeout: 300000 });
  return data?.data ?? null;
};

/** Master — alinha o 1º usuário do tenant ao usuário xprobase logado (mesmo e-mail e senha do login central). */
export const postPlatformDominioSyncTenantLogin = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/sync-tenant-login`, {});
  return data?.data ?? null;
};

/** Master — analytics agregados da base de workspaces (xprobase). */
export const getPlatformWorkspaceAnalytics = async () => {
  const { data } = await apiClient.get("/billing/admin/analytics/workspaces");
  return data?.data ?? null;
};

/** Master — oportunidades de upsell (uso vs limites do plano). */
export const getPlatformWorkspaceOpportunities = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/analytics/opportunities", { params });
  return data?.data ?? { items: [] };
};

/** Master — lista revendas (resellers). */
export const listAdminResellers = async () => {
  const { data } = await apiClient.get("/billing/admin/resellers");
  return data?.data ?? { items: [] };
};

/** Master — cria revenda. */
export const postAdminReseller = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/resellers", payload || {});
  return data?.data ?? null;
};

/** Master — vincula e-mail a portal revenda (reseller_users). */
export const postAdminResellerStaff = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/resellers/staff", payload || {});
  return data?.data ?? null;
};

/** Master — usuários xprobase (flags master / compartilhar domínios). */
export const listAdminPlatformUsers = async () => {
  const { data } = await apiClient.get("/billing/admin/platform-users");
  return data?.data ?? { items: [], meta: {} };
};

/** Master global — atualiza flags de usuário na base platform (xprobase). */
export const patchAdminPlatformUser = async (userId, payload = {}) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.patch(`/billing/admin/platform-users/${id}`, payload || {});
  return data?.data ?? null;
};

/** Master global — inclui usuário no cadastro central (xprobase). */
export const postAdminPlatformUser = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/platform-users", payload || {});
  return data?.data ?? null;
};

/** Master global — exclusão lógica do usuário no xprobase. */
export const deleteAdminPlatformUser = async (userId) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/platform-users/${id}`);
  return data?.data ?? null;
};

/** Master — IDs de domínio (xprobase) que outro usuário master pode operar (permissoes_dominios). */
export const getUserPlatformDomainsAcl = async (userId) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.get(`/users/${id}/acl`, { params: { context: "platform" } });
  return data?.data ?? { dominios: [] };
};

export const setUserPlatformDomainsAcl = async (userId, dominios = []) => {
  const id = encodeURIComponent(String(userId || "").trim());
  if (!id) throw new Error("userId é obrigatório.");
  const { data } = await apiClient.put(`/users/${id}/acl`, { dominios }, { params: { context: "platform" } });
  return data?.data ?? null;
};

/** Master — atualiza revenda (nome, status, metadata). */
export const patchAdminReseller = async (resellerId, payload = {}) => {
  const id = encodeURIComponent(String(resellerId || "").trim());
  if (!id) throw new Error("resellerId é obrigatório.");
  const { data } = await apiClient.patch(`/billing/admin/resellers/${id}`, payload || {});
  return data?.data ?? null;
};

/** Master — lista e-mails com acesso ao portal da revenda. */
export const listAdminResellerStaff = async (resellerId) => {
  const id = encodeURIComponent(String(resellerId || "").trim());
  if (!id) throw new Error("resellerId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/resellers/${id}/staff`);
  return data?.data ?? { items: [] };
};

/** Master — remove vínculo e-mail ↔ revenda. */
export const deleteAdminResellerStaff = async (resellerId, staffId) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.delete(`/billing/admin/resellers/${rid}/staff/${sid}`);
  return data?.data ?? null;
};

/** Master — atualiza nome/active do staff da revenda. */
export const patchAdminResellerStaff = async (resellerId, staffId, payload = {}) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.patch(`/billing/admin/resellers/${rid}/staff/${sid}`, payload || {});
  return data?.data ?? null;
};

/** Master — reenvia e-mail de convite (portal revenda). */
export const postAdminResellerStaffResendInvite = async (resellerId, staffId) => {
  const rid = encodeURIComponent(String(resellerId || "").trim());
  const sid = encodeURIComponent(String(staffId || "").trim());
  if (!rid || !sid) throw new Error("resellerId e staffId são obrigatórios.");
  const { data } = await apiClient.post(`/billing/admin/resellers/${rid}/staff/${sid}/resend-invite`, {});
  return data?.data ?? null;
};

/** Portal revenda — contexto. */
export const getPartnerBillingContext = async () => {
  const { data } = await apiClient.get("/billing/partner/context");
  return data?.data ?? null;
};

/** Portal revenda — workspaces da revenda (somente GET /billing/partner/dominios; sem fallback admin). */
export const listPartnerDominios = async (params = {}) => {
  const { data } = await apiClient.get("/billing/partner/dominios", { params });
  return data?.data ?? { items: [], meta: {} };
};

/** Portal revenda — detalhe workspace (mesmo payload admin com escopo). */
export const getPartnerDominioDetail = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/partner/dominios/${id}`);
  return data?.data ?? null;
};

/** Portal revenda — exclusão lógica (excluido=1 em xprobase.dominios), sem remover schema do tenant. */
export const excludePartnerDominio = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/partner/dominios/${id}/exclude`, {});
  return data?.data ?? null;
};

/** Portal revenda — catálogo de preços referência. */
export const getPartnerCatalog = async () => {
  const { data } = await apiClient.get("/billing/partner/catalog");
  return data?.data ?? { items: [], price_overrides_for_customers: {} };
};

/** Portal revenda — grava preços sugeridos ao cliente final (metadata revenda). */
export const putPartnerPricingOverrides = async (payload = {}) => {
  const { data } = await apiClient.put("/billing/partner/pricing", payload || {});
  return data?.data ?? null;
};

/* =====================================================================
 * Workspaces de apresentação (revenda + master)
 * ===================================================================== */

/** Normaliza payload de criação (camelCase + compat com dominio_id numérico). */
function normalizeWorkspaceCreateApiData(raw) {
  if (raw == null || typeof raw !== "object") return raw;
  const fromId = raw.dominioId ?? raw.dominio_id;
  const n = Number(fromId);
  const dominioId = Number.isFinite(n) && n > 0 ? n : null;
  return dominioId != null && dominioId !== raw.dominioId ? { ...raw, dominioId } : raw;
}

/** Revenda — cria workspace (mode = 'trial' | 'pay' | 'presentation'). */
export const createPartnerWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/partner/dominios", payload || {});
  return normalizeWorkspaceCreateApiData(data?.data ?? null);
};

/** Revenda — status do pipeline de apresentação (polling). */
export const getPartnerWorkspacePresentationStatus = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/partner/dominios/${id}/presentation/status`);
  return data?.data ?? null;
};

/** Revenda — converte workspace de apresentação em trial/pago. */
export const convertPartnerWorkspacePresentation = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(
    `/billing/partner/dominios/${id}/presentation/convert`,
    payload || {}
  );
  return data?.data ?? null;
};

/** Revenda — descarta workspace de apresentação (exclusão lógica + status discarded; schema do tenant preservado). */
export const discardPartnerWorkspacePresentation = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/partner/dominios/${id}/presentation`);
  return data?.data ?? null;
};

/** Master — cria workspace via wizard (suporta mode = 'trial' | 'pay' | 'presentation'). */
export const createPlatformWorkspace = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/dominios", payload || {});
  return normalizeWorkspaceCreateApiData(data?.data ?? null);
};

/** Master — status do pipeline de apresentação. */
export const getPlatformWorkspacePresentationStatus = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/presentation/status`);
  return data?.data ?? null;
};

/** Master — converte apresentação em trial/pago. */
export const convertPlatformWorkspacePresentation = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(
    `/billing/admin/dominios/${id}/presentation/convert`,
    payload || {}
  );
  return data?.data ?? null;
};

/** Master — descarta apresentação (exclusão lógica; schema do tenant preservado). */
export const discardPlatformWorkspacePresentation = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/dominios/${id}/presentation`);
  return data?.data ?? null;
};

/** Master — operadores humanos “online” em todos os workspaces com schema resolvível (mesma regra que is_online na listagem de usuários). */
export const getPlatformOnlineOperators = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/platform/online-operators", { params });
  return data?.data ?? { items: [], meta: {} };
};

/** Master — detalhe admin: domínio + assinatura + sinais de uso. */
export const getPlatformDominioAdminDetail = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}`);
  return data?.data ?? null;
};

/** Master — snapshot operacional no tenant (Customer Health parcial): leads, canais, retenção de equipe, tempos. */
export const getPlatformDominioHealthSnapshot = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/health-snapshot`, { params });
  return data?.data ?? null;
};

/** Master — vínculo Superlógica: estado atual. */
export const getPlatformDominioSuperlogicaLink = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/link`);
  return data?.data ?? null;
};

/** Master — tenta vincular por CPF/CNPJ (docValue) ou força por superlogicaClienteId. */
export const postPlatformDominioSuperlogicaLink = async (dominioId, payload = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/superlogica/link`, payload || {});
  return data?.data ?? null;
};

/** Master — remove vínculo Superlógica. */
export const deletePlatformDominioSuperlogicaLink = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/dominios/${id}/superlogica/link`);
  return data?.data ?? null;
};

/** Master — resumo financeiro/contratual (Superlógica) do cliente vinculado. */
export const getPlatformDominioSuperlogicaSummary = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/summary`);
  return data?.data ?? null;
};

/** Master — cobranças Superlógica + NFs vinculadas (aba Faturas). */
export const getPlatformDominioSuperlogicaCobrancas = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/superlogica/cobrancas`);
  return data?.data ?? null;
};

/** Master — catálogo Superlógica (somente leitura): type=planos|produtos */
export const getBillingSuperlogicaCatalogAdmin = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/superlogica/catalog", { params });
  return data?.data ?? { items: [] };
};

/** Master — importa planos (plan_products) e/ou produtos (addon_products) a partir da API Superlógica */
export const postBillingSuperlogicaImportAdmin = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/superlogica/import", payload || {});
  return data?.data ?? {};
};

/** Master — exclusão lógica: excluido=1 em xprobase.dominios (legado Xpro). */
export const excludePlatformDominio = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/exclude`, {});
  return data?.data ?? null;
};

/** Master — resumo de uso agregado (usage_daily_aggregates) por domínio. */
export const getPlatformDominioUsageSummary = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/usage/summary`, { params });
  return data?.data ?? null;
};

/** Master — faturas xprobase do domínio. */
export const listPlatformDominioInvoices = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/invoices`, { params });
  return data?.data ?? { items: [] };
};

/** Master — analytics de ofertas (billing_offer_events) por domínio. */
export const getPlatformDominioOfferAnalytics = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/offers/analytics`);
  return data?.data ?? { items: [] };
};

/** Master — vendas de passagens / funil / pagamentos (tenant) para segmento rodoviário. */
export const getPlatformDominioPassengerSales = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/sales`, { params });
  return data?.data ?? null;
};

/** Master — conciliação Gupshup (API parceiro) × mensagens no tenant × métricas billing. */
export const getPlatformDominioGupshupReconciliation = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/gupshup/reconciliation`, { params });
  return data?.data ?? null;
};

/** Master — auditoria de billing por domínio. */
export const listPlatformBillingAudit = async (dominioId, params = {}) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get("/billing/admin/audit", { params: { dominioId: id, ...params } });
  return data?.data ?? { items: [] };
};

export const getBillingAddonCatalogAdmin = async () => {
  const { data } = await apiClient.get("/billing/admin/addons");
  return data?.data ?? { items: [] };
};

export const upsertBillingAddonPriceAdmin = async (sku, payload = {}) => {
  const s = encodeURIComponent(String(sku || "").trim().toLowerCase());
  if (!s) throw new Error("sku é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/addons/${s}/prices`, payload || {});
  return data?.data ?? null;
};

/** Master — lê `workspace.segment` no tenant vinculado ao domínio (xprobase.dominios.bd). */
export const getPlatformDominioWorkspaceSegment = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/workspace-segment`);
  return data?.data ?? null;
};

/** Master — grava `workspace.segment` no tenant (configuracoes). */
export const setPlatformDominioWorkspaceSegment = async (dominioId, segment) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/dominios/${id}/workspace-segment`, { segment });
  return data?.data ?? null;
};

/** Master — catálogo unificado de segmentos (setup + console). */
export const listPlatformWorkspaceSegmentCatalog = async (params = {}) => {
  const { data } = await apiClient.get("/billing/admin/workspace-segments", { params });
  return data?.data ?? { items: [] };
};

export const createPlatformWorkspaceSegmentCatalog = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/admin/workspace-segments", payload || {});
  return data?.data ?? null;
};

export const updatePlatformWorkspaceSegmentCatalog = async (segmentKey, payload = {}) => {
  const k = encodeURIComponent(String(segmentKey || "").trim());
  if (!k) throw new Error("segmentKey é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/workspace-segments/${k}`, payload || {});
  return data?.data ?? null;
};

export const deletePlatformWorkspaceSegmentCatalog = async (segmentKey) => {
  const k = encodeURIComponent(String(segmentKey || "").trim());
  if (!k) throw new Error("segmentKey é obrigatório.");
  const { data } = await apiClient.delete(`/billing/admin/workspace-segments/${k}`);
  return data?.data ?? null;
};

/** Master — lê `workspace.ai.context.v1` no tenant vinculado ao domínio. */
export const getPlatformDominioAiWorkspaceContext = async (dominioId) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/ai-workspace-context`);
  return data?.data ?? null;
};

/** Master — merge parcial em `workspace.ai.context.v1` no tenant. */
export const setPlatformDominioAiWorkspaceContext = async (dominioId, patch) => {
  const id = encodeURIComponent(String(dominioId || "").trim());
  if (!id) throw new Error("dominioId é obrigatório.");
  const { data } = await apiClient.put(`/billing/admin/dominios/${id}/ai-workspace-context`, patch || {});
  return data?.data ?? null;
};

/** Billing — limites/preços do plano ativo (sem catálogo admin completo). */
export const getBillingPlanCurrentCatalog = async () => {
  const { data } = await apiClient.get("/billing/plan/current-catalog");
  return data?.data ?? { items: [] };
};

export const getBillingInvoices = async () => {
  const { data } = await apiClient.get("/billing/invoices");
  return data?.data ?? { items: [] };
};

export const postBillingCheckoutIntent = async (payload = {}) => {
  const { data } = await apiClient.post("/billing/checkout/intent", payload || {});
  return data?.data ?? null;
};

export const getBillingOffers = async (params = {}) => {
  const { data } = await apiClient.get("/billing/offers", { params });
  return data?.data ?? { items: [] };
};

export const getBillingOfferAnalytics = async () => {
  const { data } = await apiClient.get("/billing/offers/analytics");
  return data?.data ?? { items: [] };
};

export const viewBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/view`, {});
  return data?.data ?? null;
};

export const clickBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/click`, {});
  return data?.data ?? null;
};

export const acceptBillingOffer = async (offerId) => {
  const id = encodeURIComponent(String(offerId || "").trim());
  if (!id) throw new Error("offerId é obrigatório.");
  const { data } = await apiClient.post(`/billing/offers/${id}/accept`, {});
  return data?.data ?? null;
};

export const seedOfficialBillingAddonPackages = async () => {
  const { data } = await apiClient.post("/billing/admin/addons/seed-official", {});
  return data?.data ?? null;
};

export const seedTelephonySmsAddonPrices = async () => {
  const { data } = await apiClient.post("/billing/admin/addons/seed-telephony-sms", {});
  return data?.data ?? null;
};

/**
 * Billing Admin - vincula assinatura de um domínio a um plano.
 * payload esperado:
 * { planSku, version?, dominioId? | company?, billingMode?, enforceLimits?, status?, resellerId?, billingAccountId? }
 */
export const assignBillingSubscription = async (payload) => {
  const { data } = await apiClient.post("/billing/admin/subscriptions/assign", payload || {});
  return data?.data ?? null;
};

export const createBillingPlan = async (payload) => {
  const { data } = await apiClient.post("/billing/admin/plans", payload || {});
  return data?.data ?? null;
};

export const createBillingPlanVersion = async (sku, payload) => {
  const { data } = await apiClient.post(`/billing/admin/plans/${encodeURIComponent(sku)}/versions`, payload || {});
  return data?.data ?? null;
};

export const updateBillingPlanLimits = async (sku, version, limits) => {
  const { data } = await apiClient.put(
    `/billing/admin/plans/${encodeURIComponent(sku)}/versions/${encodeURIComponent(version)}/limits`,
    { limits: Array.isArray(limits) ? limits : [] }
  );
  return data?.data ?? null;
};

/**
 * Billing Admin — MRR e setup (centavos) por audiência em plan_version_prices.
 * @param {string} sku
 * @param {number|string} version
 * @param {{ audience: 'end_customer'|'reseller', amountMrrCents: number, amountSetupCents: number, currencyCode?: string }} payload
 */
export const upsertBillingPlanVersionPrices = async (sku, version, payload) => {
  const { data } = await apiClient.put(
    `/billing/admin/plans/${encodeURIComponent(sku)}/versions/${encodeURIComponent(version)}/prices`,
    payload || {}
  );
  return data?.data ?? null;
};

export const consumeBillingMetric = async (payload) => {
  const { data } = await apiClient.post("/billing/usage/consume", payload || {});
  return data?.data ?? null;
};

/**
 * Apaga no provedor (ex.: WhatsApp Cloud API) quando o canal suporta, e remove o registro da listagem.
 */
export const deleteConversationMessage = async (conversationId, messageId) => {
  if (conversationId == null || messageId == null) {
    throw new Error("conversationId e messageId são obrigatórios.");
  }
  return fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/messages/${encodeURIComponent(String(messageId))}`,
    "DELETE"
  );
};

export const getConversationInsights = async (id) => {
  const res = await fetchData(`/conversations/${id}/insights`, "GET");
  return res?.data ?? res;
};

/** Analytics agregado de um atendente (solicitações com l.id_usuario = id). Query: start, end (YYYY-MM-DD). */
export const getUserOperatorAnalytics = async (userId, query = {}) => {
  const q = new URLSearchParams();
  if (query.start) q.set("start", query.start);
  if (query.end) q.set("end", query.end);
  const qs = q.toString();
  const suffix = qs ? `?${qs}` : "";
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/operator-analytics${suffix}`, "GET");
  return res?.data ?? res;
};

/** Presign S3 para upload de PDF (PDA/DISC etc.) vinculado ao usuário. */
export const presignUserOperatorDocument = async (userId, body) => {
  const res = await fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/presign`,
    "POST",
    body
  );
  return res?.data ?? {};
};

/** Após PUT no S3, dispara extração, chunking e embeddings. */
export const ingestUserOperatorDocument = async (userId, body) => {
  const res = await fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/ingest`,
    "POST",
    body
  );
  return res?.data ?? res;
};

export const listUserOperatorDocuments = async (userId) => {
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/operator-documents`, "GET");
  const d = res?.data;
  if (d && Array.isArray(d.items)) return d.items;
  if (Array.isArray(d)) return d;
  return [];
};

export const deleteUserOperatorDocument = async (userId, docId) => {
  return fetchData(
    `/users/${encodeURIComponent(String(userId))}/operator-documents/${encodeURIComponent(String(docId))}`,
    "DELETE"
  );
};

/**
 * Gera explicação/insights de IA para métricas do dashboard.
 * @param {string} scope - ex.: "atendimento"
 * @param {object} payload - dados da métrica + contexto do período/filtros
 */
export const getDashboardMetricInsights = async (scope, payload = {}) => {
  if (!scope) throw new Error("scope é obrigatório para gerar insights de métrica.");
  const res = await fetchData(`/dashboard/${scope}/metric-insights`, "POST", payload);
  return res?.data ?? res;
};

/**
 * Chat com IA restrito a uma métrica do dashboard.
 * @param {string} scope - ex.: "atendimento" | "clientes" | "produtos" | "vendas"
 * @param {object} payload - basePayload + userMessage + history
 */
export const chatDashboardMetric = async (scope, payload = {}) => {
  if (!scope) throw new Error("scope é obrigatório para conversar sobre a métrica.");
  const res = await fetchData(`/dashboard/${scope}/metric-chat`, "POST", payload);
  return res?.data ?? res;
};

export const createAssistantSession = async (payload = {}) => {
  const res = await fetchData(`/assistant/session`, "POST", payload);
  return res?.data ?? res;
};

export const chatWithAssistant = async (payload = {}) => {
  const res = await fetchData(`/assistant/chat`, "POST", payload);
  return res?.data ?? res;
};

export const generateBannerDraftWithAi = async (payload = {}) => {
  const res = await fetchData(`/banners/ai-draft`, "POST", payload);
  return res?.data ?? res;
};

const MOCK_TRAINING_HUB = [
  {
    id: 901,
    titulo: "Onboarding (mock CMS)",
    slug: "onboarding-mock-cms",
    ordem: 1,
    updated_at: "2026-03-01",
    training: {
      format: "training_kb.v1",
      intro: "Dados estáticos quando REACT_APP_MOCK_CMS=1.",
      modules: [
        {
          id: "mm1",
          title: "Links úteis",
          order: 0,
          items: [
            {
              id: "mi1",
              kind: "link",
              title: "Xpro",
              url: "https://xpro.me",
              notes: "",
              order: 0,
            },
          ],
        },
      ],
    },
  },
];

/** Lista treinamentos ativos (materiais de ajuda) para o hub. */
export const fetchTrainingHub = async () => {
  if (typeof process !== "undefined" && process.env.REACT_APP_MOCK_CMS === "1") {
    await new Promise((r) => setTimeout(r, 120));
    return MOCK_TRAINING_HUB;
  }
  const res = await fetchData("/training/hub", "GET");
  const raw = res?.data ?? res;
  return Array.isArray(raw) ? raw : [];
};

/** Sessão de IA escopada a um treinamento (conteúdo cadastrado). */
export const createTrainingAssistantSession = async (payload = {}) => {
  const res = await fetchData(`/assistant/training/session`, "POST", payload);
  return res?.data ?? res;
};

export const chatTrainingAssistant = async (payload = {}) => {
  const res = await fetchData(`/assistant/training/chat`, "POST", payload);
  return res?.data ?? res;
};

export const getConversationById = async (id) => {
  const res = await fetchData(`/conversations/${id}`, "GET");
  return res?.data ?? res;
};

export const getRequestDetails = async (requestId) => {
  const res = await fetchData(`/requests/${encodeURIComponent(String(requestId))}`, "GET");
  return res?.data ?? res;
};

/**
 * Verifica se existem conversas abertas (status Pendente ou Em Atendimento)
 * para um determinado telefone de cliente, opcionalmente filtrando por projeto.
 * Usamos a própria listagem de /conversations com filtros de status.
 */
export const checkOpenConversationsByPhone = async ({ phone, projectId } = {}) => {
  const sp = new URLSearchParams();
  if (phone) {
    sp.set("search", String(phone).trim());
  }
  // Foco em relacionamento (chat) e apenas status 0/1
  sp.set("tipo_scope", "relacionamento");
  sp.set("filters", ["Pendente", "Em Atendimento"].join(","));
  sp.set("limit", "10");
  if (projectId != null) {
    sp.set("ids_projetos", String(projectId));
  }
  const qs = sp.toString();
  const res = await fetchData(`/conversations${qs ? `?${qs}` : ""}`, "GET");
  const list = res?.data ?? res ?? [];
  return Array.isArray(list) ? list : [];
};

/**
 * Listagem de conversas com os mesmos query params da tela de chat.
 * Útil para painéis (ex.: dashboard ao vivo). `default_date_window_days=0` evita cortar por data.
 */
/** Séries diárias de solicitações (leads) atribuídas ao chatbot URA — GET /chatbot/analytics/conversation-series */
export const getChatbotConversationSeries = async (userId, opts = {}) => {
  const qs = new URLSearchParams();
  qs.set("user_id", String(userId));
  qs.set("days", String(Math.min(Math.max(Number(opts.days) || 30, 1), 90)));
  if (opts.tipo_scope) qs.set("tipo_scope", String(opts.tipo_scope));
  const res = await fetchData(`/chatbot/analytics/conversation-series?${qs.toString()}`, "GET");
  return res?.data ?? {};
};

export const getConversationsList = async (opts = {}) => {
  const qs = new URLSearchParams();
  const lim = Math.min(parseInt(opts.limit, 10) || 50, 50);
  qs.set("limit", String(lim));
  const ts = String(opts.tipo_scope || "relacionamento").trim().toLowerCase();
  const tipoScopeParam =
    ts === "comercial" || ts === "commercial" || ts === "0"
      ? "comercial"
      : ts === "todos" || ts === "ambos" || ts === "all"
        ? "todos"
        : "relacionamento";
  qs.set("tipo_scope", tipoScopeParam);
  qs.set("default_date_window_days", String(opts.default_date_window_days ?? 0));
  if (Array.isArray(opts.filters) && opts.filters.length) {
    qs.set("filters", opts.filters.join(","));
  }
  if (opts.tipo_responsavel) {
    qs.set("tipo_responsavel", String(opts.tipo_responsavel));
  }
  if (opts.data_inicio) qs.set("data_inicio", String(opts.data_inicio));
  if (opts.data_fim) qs.set("data_fim", String(opts.data_fim));
  if (opts.ids_usuarios != null && opts.ids_usuarios !== "") {
    qs.set("ids_usuarios", String(opts.ids_usuarios));
  }
  const query = qs.toString();
  const res = await fetchData(`/conversations${query ? `?${query}` : ""}`, "GET");
  const list = Array.isArray(res?.data) ? res.data : [];
  return {
    list,
    pagination: res?.pagination || { hasMore: false, nextCursor: null },
  };
};

/** Usuários humanos (exclui chatbots), com flags `is_online` quando disponível no banco. */
export const getHumanOperators = async (limit = 200) => {
  const res = await fetchData(`/users?limit=${encodeURIComponent(String(limit))}&tipo_responsavel=humanos`, "GET");
  const rows = Array.isArray(res?.data) ? res.data : [];
  return { operators: rows, pagination: res?.pagination };
};

/** Encerra sessões JWT do usuário alvo (admin/master ou permissão de editar Usuários). */
export const postForceLogoutUser = async (userId) => {
  const res = await fetchData(`/users/${encodeURIComponent(String(userId))}/force-logout`, "POST", {});
  return res?.data ?? res;
};

export const generateSuggestedReply = async (conversationId, body = {}) => {
  if (!conversationId) {
    throw new Error(
      "conversationId é obrigatório para gerar sugestão de resposta."
    );
  }
  const res = await fetchData(
    `/conversations/${conversationId}/suggested-reply`,
    "POST",
    body
  );
  return res?.data ?? res;
};

export const getConversationMessageSuggestions = async (conversationId, params = {}) => {
  if (!conversationId) {
    throw new Error("conversationId é obrigatório para obter sugestões de mensagens.");
  }
  const qs = new URLSearchParams();
  if (params.originId != null) qs.set("originId", params.originId);
  if (params.typeId != null) qs.set("typeId", params.typeId);
  const query = qs.toString();
  const endpoint = `/conversations/${conversationId}/message-suggestions${query ? `?${query}` : ""}`;
  const res = await fetchData(endpoint, "GET");
  return res?.data ?? res;
};

/** Carrinho da solicitação (mesma tabela do e-commerce) */
export const putConversationCart = async (conversationId, items) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/cart`,
    "PUT",
    { items: Array.isArray(items) ? items : [] }
  );
  return res?.data ?? res;
};

/** Intenção de pagamento para o atendente (não substitui /ecom/payments/initialize) */
export const postConversationPaymentIntent = async (conversationId, body) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/payments/intent`,
    "POST",
    body || {}
  );
  return res?.data ?? res;
};

export const getConversationShippingOptions = async (conversationId, zip) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const cep = String(zip || "").replace(/\D/g, "");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/shipping-options?zip=${encodeURIComponent(cep)}`,
    "GET"
  );
  return res?.data ?? res;
};

export const putConversationShipping = async (conversationId, body) => {
  if (!conversationId) throw new Error("conversationId obrigatório.");
  const res = await fetchData(
    `/conversations/${encodeURIComponent(String(conversationId))}/shipping`,
    "PUT",
    body || {}
  );
  return res?.data ?? res;
};

export const getChannelEmailDomainStatus = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para buscar status de domínio de e-mail.");
  const res = await fetchData(`/channels/${channelId}/email-domain/status`, "GET");
  return res?.data ?? res;
};

export const sendChannelEmailDomainTest = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para enviar e-mail de teste do domínio de e-mail.");
  }
  const res = await fetchData(`/channels/${channelId}/email-domain/test-send`, "POST");
  return res?.data ?? res;
};

export const getChannelWebsiteConfig = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para obter configuração do website.");
  const res = await fetchData(`/channels/${channelId}/website`, "GET");
  return res?.data ?? res;
};

export const updateChannelWebsiteConfig = async (channelId, body) => {
  if (!channelId) throw new Error("channelId é obrigatório para atualizar configuração do website.");
  const res = await fetchData(`/channels/${channelId}/website`, "PUT", body || {});
  return res?.data ?? res;
};

export const regenerateChannelWebsiteTempUrl = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para regenerar URL temporária do website.");
  const res = await fetchData(`/channels/${channelId}/website/temp-url/regenerate`, "POST");
  return res?.data ?? res;
};

export const suggestChannelWebsiteTheme = async (channelId, prompt) => {
  if (!channelId) throw new Error("channelId é obrigatório para sugerir tema do website.");
  const res = await fetchData(`/channels/${channelId}/website/theme/suggest`, "POST", {
    prompt: String(prompt || "").trim(),
  });
  return res?.data ?? res;
};

export const getChannelWebsiteDomainStatus = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para buscar status de domínio do website.");
  const res = await fetchData(`/channels/${channelId}/website/domain/status`, "GET");
  return res?.data ?? res;
};

export const setupChannelWebsiteDomain = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para configurar domínio do website.");
  const res = await fetchData(`/channels/${channelId}/website/domain/setup`, "POST");
  return res?.data ?? res;
};

export const listChannelWebsiteAudit = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para listar histórico do website.");
  const res = await fetchData(`/channels/${channelId}/website/audit`, "GET");
  return res?.data ?? res;
};

export const getChannelTemplates = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/templates`, "GET");
  return res?.data ?? res;
};

export const importGupshupTemplates = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/gupshup/import-templates`, "POST");
  return res?.data ?? res;
};

/** Valida credenciais Gupshup (mensagens e/ou Partner voz). Body: { which?: "messaging"|"voice"|"both", messaging?, voice? } */
export const testGupshupChannelIntegrations = async (channelId, body) => {
  if (!channelId) {
    throw new Error("ID do canal é obrigatório para testar integrações Gupshup.");
  }
  const res = await fetchData(`/channels/${channelId}/gupshup/test-integrations`, "POST", body || {});
  return res?.data ?? res;
};

// === WhatsApp QRCode ===
export const createApifacilInstance = async (channelId, body) => {
  if (!channelId) throw new Error("channelId é obrigatório para criar instância WhatsApp QRCode.");
  const res = await fetchData(`/channels/${channelId}/apifacil/instance`, "POST", body);
  return res?.data ?? res;
};

export const getApifacilInstanceQrCode = async (channelId) => {
  if (!channelId) throw new Error("channelId é obrigatório para obter QRCode da instância WhatsApp QRCode.");
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/qrcode`, "GET");
  return res?.data ?? res;
};

export const getApifacilInstanceStatus = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para obter status da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/instance/status`, "GET");
  return res?.data ?? res;
};

export const getApifacilInstanceConfig = async (channelId) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para obter configuração da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/config`, "GET");
  return res?.data ?? res;
};

export const updateApifacilInstanceConfig = async (channelId, body) => {
  if (!channelId) {
    throw new Error("channelId é obrigatório para atualizar configuração da instância WhatsApp QRCode.");
  }
  const res = await fetchData(`/channels/${channelId}/apifacil/config`, "PUT", body);
  return res?.data ?? res;
};

// === Integrações Meta (Facebook / Instagram) ===
export const getMetaOauthUrl = async (channelType) => {
  const safeType = channelType || "generic";
  const res = await fetchData(`/channels/meta/oauth-url?channelType=${encodeURIComponent(safeType)}`, "GET");
  // backend responde { url }
  return res?.data?.url ?? res?.url;
};

export const getMetaPages = async ({ accessToken, includeInstagram = true }) => {
  if (!accessToken) {
    throw new Error("accessToken é obrigatório para listar páginas da Meta.");
  }
  const res = await fetchData("/integrations/meta/pages", "POST", {
    accessToken,
    includeInstagram,
  });
  return res?.data ?? res;
};

// === Telefonia (PABX) - simulação para testes ===
/** Monta URL de telefonia com ?company= para o backend identificar o workspace e emitir no socket. */
function telephonyUrl(path) {
  const company =
    typeof localStorage !== "undefined" ? getWorkspaceDatabaseHeaderValue() : null;
  const qs = company ? `?company=${encodeURIComponent(company)}` : "";
  return `${path}${qs}`;
}

/** Simula chamada recebida (POST /telephony/incoming). Retorna { idlead, sid } em caso de sucesso. */
export const simulateTelephonyIncoming = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/incoming"), "POST", payload);
  return res;
};

/** Simula chamada terminada (POST /telephony/end). Usar com o mesmo id_chamada (sid) da chamada simulada. */
export const simulateTelephonyEnd = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/end"), "POST", payload);
  return res;
};

/** Finaliza chamada originada e contabiliza minutos outbound. */
export const simulateTelephonyEndOutbound = async (payload) => {
  const res = await fetchData(telephonyUrl("/telephony/end-outbound"), "POST", payload);
  return res;
};

export const listTwilioOwnedNumbers = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/twilio/owned-numbers`, "GET");
  return res?.data ?? res;
};

export const listTwilioAvailableNumbers = async (channelId, params = {}) => {
  const qs = toQS(params);
  const res = await fetchData(`/channels/${channelId}/twilio/available-numbers${qs}`, "GET");
  return res?.data ?? res;
};

export const purchaseTwilioNumber = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/purchase-number`, "POST", payload);
  return res?.data ?? res;
};

export const getTwilioVoiceToken = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/twilio/voice-token`, "POST", {});
  return res?.data ?? res;
};

export const createTwilioOutboundCall = async (channelId, payload) => {
  const res = await fetchData(`/channels/${channelId}/twilio/outbound-call`, "POST", payload);
  return res?.data ?? res;
};

/** Proxy para POST /{phone-number-id}/calls na Graph API (WhatsApp Calling). */
export const postWhatsappCloudCall = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/calls`, "POST", body);
  return res?.data ?? res;
};

/** Habilitar chamadas / horário comercial via POST …/settings na Graph API. */
export const postWhatsappCloudSettings = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/settings`, "POST", body);
  return res?.data ?? res;
};

export const getWhatsappCloudCallRecord = async (channelId, callId) => {
  const enc = encodeURIComponent(callId);
  const res = await fetchData(`/channels/${channelId}/whatsapp-cloud/calls/${enc}`, "GET");
  return res?.data ?? res;
};

/** Solicita permissão de chamada (interactive call_permission_request) via Partner API Gupshup. */
export const postGupshupVoiceCallPermissionRequest = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/call-permission-request`, "POST", body);
  return res?.data ?? res;
};

/** Registro/habilitação SIP na Partner API (URL em GUPSHUP_PARTNER_VOICE_REGISTER_URL ou body.registerUrl). */
export const postGupshupVoiceRegisterSip = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/register-sip`, "POST", body || {});
  return res?.data ?? res;
};

/** Sugestão de host/porta SIP (infra da plataforma) para metaData GS_SIP (Gupshup WABA). */
export const getGupshupTwilioSipPeerHints = async (channelId) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/twilio-sip-peer-hints`, "GET");
  return res?.data ?? res;
};

/** Retorna hints para o SBC originar INVITE TLS à borda Gupshup (após validar permissão). */
export const postGupshupSipOutboundHints = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/sip-outbound-hints`, "POST", body);
  return res?.data ?? res;
};

/**
 * Liga o agente (softphone/PSTN) e, ao atender, conecta SIP à borda Gupshup (WhatsApp).
 * Body: { to, agentTo, leadId?, sipRegion? }
 */
export const postGupshupTwilioWhatsappOutboundCall = async (channelId, body) => {
  const res = await fetchData(`/channels/${channelId}/gupshup-voice/twilio-outbound-call`, "POST", body);
  return res?.data ?? res;
};

export const getTypeQuickReplies = async (typeId) => {
  const res = await fetchData(`/types/${typeId}/quick-replies`, "GET");
  return res?.data ?? [];
};

export const createTypeQuickReply = async (typeId, body) => {
  const res = await fetchData(`/types/${typeId}/quick-replies`, "POST", body);
  return res?.data ?? res;
};

export const updateTypeQuickReply = async (typeId, replyId, body) => {
  const res = await fetchData(`/types/${typeId}/quick-replies/${replyId}`, "PUT", body);
  return res?.data ?? res;
};

export const deleteTypeQuickReply = async (typeId, replyId) => {
  await fetchData(`/types/${typeId}/quick-replies/${replyId}`, "DELETE");
};

export const testPassagensConnection = async (target) => {
  if (!target) throw new Error("target é obrigatório para testar conexão.");
  const res = await fetchData("/configurations/passagens/test", "POST", { target });
  return res?.data ?? res;
};

/** Inicia importação de trechos/passagens (assíncrona com worker quando disponível, ou síncrona). */
export const importPassagensErp = async (body = {}) => {
  const res = await fetchData("/products/import-passagens-erp", "POST", body);
  return res?.data ?? res;
};

/** Acompanha job de importação (Redis), retornado pelo POST acima quando `accepted: true`. */
export const getImportPassagensErpJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/import-passagens-erp/jobs/${id}`, "GET");
  return res?.data ?? res;
};

/** Solicita cancelamento do job assíncrono de importação. */
export const cancelImportPassagensErpJob = async (jobId) => {
  const id = encodeURIComponent(String(jobId || ""));
  const res = await fetchData(`/products/import-passagens-erp/jobs/${id}/cancel`, "POST", {});
  return res?.data ?? res;
};

// ============================================================================
// AGENTES DE IA
// ============================================================================

const toQS = (obj = {}) => {
  const sp = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (s) sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

/** Lista cidades do cadastro de passagens (segmento transporte). */
export const getPassengerCities = async (params = {}) => {
  const { page = 1, limit = 50, q = "" } = params;
  const qs = toQS({ page, limit, ...(q ? { q } : {}) });
  const res = await fetchData(`/products/passenger-cities${qs}`, "GET");
  return res?.data ?? res;
};

export const getAgents = async (params = {}) => {
  const { page = 1, limit = 20, search = "" } = params;
  const qs = toQS({ page, limit, search });
  return fetchData(`/agents${qs}`);
};

export const getAgentById = async (id) => {
  const res = await fetchData(`/agents/${id}`, "GET");
  return { data: res?.data ?? res };
};

export const createAgent = async (data) => {
  return fetchData("/agents", "POST", data);
};

export const updateAgent = async (id, data) => {
  return fetchData(`/agents/${id}`, "PUT", data);
};

export const deleteAgent = async (id) => {
  return fetchData(`/agents/${id}`, "DELETE");
};

export const getAgentLogs = async (id, params = {}) => {
  const { page = 1, limit = 50 } = params;
  const qs = toQS({ page, limit });
  const res = await fetchData(`/agents/${id}/logs${qs}`);
  return { data: res?.data ?? res ?? [], pagination: res?.pagination };
};

/** Envia documentos (Word, planilhas, PDF) para o agente. FormData com campo "documents". */
export const uploadAgentDocuments = async (id, files) => {
  if (!id || !files?.length) return { data: null };
  const formData = new FormData();
  files.forEach((file) => formData.append("documents", file));
  const response = await apiClient.request({
    method: "POST",
    url: `/agents/${id}/documents`,
    data: formData,
  });
  return response?.data ?? response;
};

// ============================================================================
// EXPORTAÇÃO DO DASHBOARD (CSV)
// ============================================================================

/** Escapa um campo para formato CSV (delimitador ;) */
const __csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[;"\n\r,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Monta o CSV a partir de linhas e cabeçalhos fixos (separador ;) */
function __buildCsv(rows, headers) {
  const BOM = "\uFEFF"; // compat Excel
  const head = headers.join(";");
  const body = rows
    .map((r) => headers.map((h) => __csvEscape(r[h])).join(";"))
    .join("\n");
  return `${BOM}${head}\n${body}\n`;
}

/** Normaliza o payload dos endpoints /dashboard/{section}/charts em linhas "long format" */
function __normalizeDashboardToRows(section, payload) {
  const out = [];
  if (!payload || typeof payload !== "object") return out;

  const period = payload.period || {};
  const pStart = period.start || "";
  const pEnd = period.end || "";

  const add = (metric, dim1, dim2, value) =>
    out.push({
      secao: section,
      metrica: metric,
      dim1: dim1 ?? "",
      dim2: dim2 ?? "",
      valor: value ?? 0,
      inicio: pStart,
      fim: pEnd,
    });

  // Cards numéricos
  if (payload.cards && typeof payload.cards === "object") {
    Object.entries(payload.cards).forEach(([k, v]) => {
      if (typeof v === "number") add("cards", k, "", v);
    });
  }

  const ch = payload.charts || {};

  if (section === "atendimento") {
    (ch.emProcessamento || []).forEach((r) =>
      add("emProcessamento", r.name, "", r.y ?? r.value ?? 0)
    );
    (ch.finalizados || []).forEach((r) =>
      add("finalizados", r.name, "", r.y ?? r.value ?? 0)
    );
    (ch.serieTemporal || []).forEach((r) =>
      add("serieTemporal", r.day || r.date, "", r.value ?? 0)
    );
    (ch.motivos || []).forEach((n) =>
      add("motivos", n.name, "", n.value ?? 0)
    );
  }

  if (section === "produtos") {
    (ch.abc || []).forEach((r) => {
      add("abc", r.label, "revenue", r.revenue);
      add("abc", r.label, "cumul", r.cumul);
    });
    (ch.convByCat || []).forEach((r) => add("convByCat", r.name, "", r.value));
    (ch.oportunidades || []).forEach((r) =>
      add("oportunidades", r.label, "", r.gapScore)
    );
    (ch.mixCat || []).forEach((r) => add("mixCat", r.name, "", r.value));
    (ch.mixDisp || []).forEach((r) => add("mixDisp", r.name, "", r.value));
    (ch.monthlyAgg || []).forEach((r) => {
      add("monthlyAgg", r.month, "quantity", r.quantity);
      add("monthlyAgg", r.month, "revenue", r.revenue);
      add("monthlyAgg", r.month, "conv", r.conv);
    });
  }

  if (section === "vendas") {
    (ch.revenueByCategory || []).forEach((r) =>
      add("revenueByCategory", r.category, "", r.revenue)
    );
    (ch.salesByChannel || []).forEach((r) =>
      add("salesByChannel", r.name, "", r.value)
    );
    (ch.monthlyOverview || []).forEach((r) => {
      add("monthlyOverview", r.month, "orders", r.orders);
      add("monthlyOverview", r.month, "revenue", r.revenue);
    });
  }

  if (section === "venda-passagens") {
    (ch.origins || []).forEach((r) => add("origins", r.name, "", r.qty));
    (ch.destinations || []).forEach((r) => add("destinations", r.name, "", r.qty));
    (ch.topRoutes || []).forEach((r) => add("topRoutes", r.route, "", r.qty));
    (ch.dailySeries || []).forEach((r) => {
      add("dailySeries", r.date, "tickets", r.tickets);
      add("dailySeries", r.date, "gmv", r.gmv);
    });
  }

  if (section === "clientes") {
    // fallback genérico: exporta arrays de objetos qualquer como métrica => valor
    const arrays = Object.entries(ch).filter(([, v]) => Array.isArray(v));
    arrays.forEach(([metric, arr]) => {
      arr.forEach((r) => {
        if (typeof r === "object") {
          const keys = Object.keys(r);
          const first =
            r.name ||
            r.stage ||
            r.category ||
            r.region ||
            r.label ||
            keys[0];
          const val =
            r.value ??
            r.percentage ??
            r.qtd ??
            r.count ??
            r.total ??
            r.y ??
            0;
          add(metric, first, "", val);
        }
      });
    });
  }

  return out;
}

/**
 * Faz o download de um CSV do dashboard.
 * - section: "atendimento" | "clientes" | "produtos" | "vendas" | "venda-passagens"
 * - params: { start, end, ... }
 */
export const exportDashboardCsv = async (section, params = {}, fileName) => {
  // monta querystring local (sem depender de toQS externo)
  const qs = new URLSearchParams(Object.entries(params).reduce((acc, [k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") acc[k] = String(v);
    return acc;
  }, {})).toString();

  const endpoint = `/dashboard/${section}/charts${qs ? `?${qs}` : ""}`;
  const resp = await fetchData(endpoint, "GET");
  const payload = resp?.data || resp || {};

  const rows = __normalizeDashboardToRows(section, payload);
  if (!rows.length) throw new Error("Sem dados para exportar nesse período.");

  const headers = ["secao", "metrica", "dim1", "dim2", "valor", "inicio", "fim"];
  const csv = __buildCsv(rows, headers);

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp =
    (params?.start || "").replace(/-/g, "") +
    "_" +
    (params?.end || "").replace(/-/g, "");
  a.href = url;
  a.download = fileName || `dashboard_${section}_${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const fetchPublicSurvey = async (slug, company, token = null) => {
  const query = new URLSearchParams();
  query.set("company", company);
  if (token) query.set("token", token);
  const { data } = await apiClient.get(`/public/surveys/${slug}?${query.toString()}`, {
    headers: { Company: company },
  });
  const payload = data?.data ?? data;
  return { data: payload ?? null };
};

export const startPublicSurveySession = async (slug, company, payload = {}) => {
  const query = new URLSearchParams({ company }).toString();
  const { data } = await apiClient.post(`/public/surveys/${slug}/sessions?${query}`, payload, {
    headers: { Company: company, "Content-Type": "application/json" },
  });
  const inner = data?.data ?? data;
  return { data: inner ?? null };
};

export const submitPublicSurveyAnswers = async (slug, company, token, answers = []) => {
  const query = new URLSearchParams({ company }).toString();
  const { data } = await apiClient.post(
    `/public/surveys/${slug}/sessions/${token}/complete?${query}`,
    { answers },
    {
      headers: { Company: company, "Content-Type": "application/json" },
    }
  );
  const inner = data?.data ?? data;
  return { data: inner ?? null };
};

/** @returns {Promise<Array>} */
export const fetchOperatorNotifications = async (limit = 50) => {
  const res = await fetchData(
    `/operator-notifications?limit=${encodeURIComponent(String(limit))}`,
    "GET"
  );
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

export const appendOperatorNotification = async (payload) => {
  return fetchData("/operator-notifications", "POST", payload);
};

export const markOperatorNotificationsRead = async (ids) => {
  return fetchData("/operator-notifications/mark-read", "POST", { ids });
};

export const fetchPushVapidPublicKey = async () => {
  const res = await fetchData("/push/vapid-public-key", "GET");
  const d = res?.data;
  if (d && typeof d === "object" && d.publicKey) return d.publicKey;
  return null;
};

export const subscribeWebPush = async (subscriptionObject) => {
  return fetchData("/push/subscribe", "POST", { subscription: subscriptionObject });
};

export { apiClient };
